import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Body7, Container, Dialog, DialogButtonRow } from '@ui';
import { FlatButton, GhostButton, TransparentFlatButton } from '@ui/buttons';
import { DRILL_DOWN_REASONS } from './memberFeedId';
import { formatFeedId } from './format';

// The rival ids are the explanation in the ambiguous case, so they're set in mono at full length
// rather than humanized: the analyst is going to compare them character by character.
const CandidateList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Candidate = styled.li`
  font-family: monospace;
  color: ${({ theme }) => theme.themeColors.text};
`;

const CandidateText = styled.div`
  padding: 2px 0;
`;

// A raw <button> for the same reason MemberMarkerCell uses one: the shared buttons are CTA-shaped,
// and these have to stay a list of ids the analyst is comparing character by character. The border
// is transparent until hover so the resting list reads as text, which is what it is until you pick.
const CandidateButton = styled(GhostButton)`
  display: block;
  width: 100%;
  padding: 2px 6px;
  margin-left: -6px;
  /* Transparent rather than absent: it reserves the space the hover border fills, so a candidate
     does not shift 2px under the pointer. */
  border: 1px solid transparent;
  border-radius: 4px;

  &:hover {
    border-color: ${({ theme }) => theme.themeColors.primaryCtaButton};
  }

  &:focus-visible {
    outline-offset: 1px;
  }
`;

const Key = styled.span`
  font-family: monospace;
`;

// What to say, and what to do about it. Kept beside the reason codes so a new code can't ship
// without copy, and phrased as "what would fix this" — a dead end with no next step reads as a bug.
//
// `allowsTryAnyway` travels here rather than being decided at the render site for the same reason:
// the offer and the copy that leads into it have to agree. It is set only where sending the raw key
// is a sensible guess — see NOT_IN_REGISTRY below.
const EXPLANATIONS = {
  [DRILL_DOWN_REASONS.NOT_IN_REGISTRY]: {
    title: 'No feed in the list matches this component',
    what: 'Nothing in the feed list we loaded matches this component, so there is nothing to send you to automatically.',
    // Three causes, and the list can't tell them apart. The first is about the spec; the other two
    // are about the list itself, which GET /feeds documents as incomplete by construction.
    why: 'Either the component is computed inside its parent spec and was never published on its own, or it is a derived source-table feed, which the feed list omits, or the list came back short — a bad multi-input registry fails open server-side, returning the score feeds and dropping the rest.',
    next: 'Because that list is a set of suggestions rather than a roster, the id is still worth sending — only the server can say whether the feed exists. Failing that, check the component against the spec in Advanced Score Specs.',
    allowsTryAnyway: true,
  },
  [DRILL_DOWN_REASONS.AMBIGUOUS]: {
    title: 'More than one feed could be meant',
    what: 'Several registered feeds match this component’s name, and the row doesn’t say which one produced the value.',
    why: 'The response identifies members by bare name, not by feed id, so two versions of the same spec — or a spec and a wrapper around it — are indistinguishable from here.',
    next: 'Open the one you want directly in the feed field above. Guessing on your behalf could show you a different version’s numbers under this column’s heading.',
    // Same refusal, different remedy. Naming the rivals and then leaving them inert made the ids
    // look pressable and do nothing — and sent the analyst off to retype an id the modal was
    // already displaying. When the parent can act on a pick, the list becomes the control: the app
    // still refuses to choose, and the analyst choosing is exactly what makes it safe.
    nextWhenPickable:
      'Pick the one you want below. The app won’t choose for you — guessing could show you a different version’s numbers under this column’s heading — but you can.',
    allowsPick: true,
  },
  [DRILL_DOWN_REASONS.NO_IDENTITY]: {
    title: 'This component didn’t arrive with an id',
    what: 'The row carries a value for this component but no name or feed id for it, so there is nothing to look up.',
    why: 'That points at the response rather than the spec: the feed reported a member without naming it.',
    // The old remedy here said to re-run with slim mode off. It could never apply: slim drops the
    // `inputs` object entirely, so no marker renders, so a slim read can't be what you pressed.
    next: 'Nothing about the read can be changed to recover the id — this one already had slim off, or there would have been no marker to press. Look up the parent spec’s inputs in Advanced Score Specs for the member’s real name, then open that feed by id in the field above.',
  },
};

// Why a marker you just pressed isn't going to open. Mounted by the parent: {state && <Modal … />}.
//
// The alternative was leaving these markers inert, which is what shipped first — but an inert cell
// can't distinguish "not viewable" from "the app is broken", and the analyst has no way to ask.
//
// `onOpenAnyway(memberKey)` is optional: when the parent supplies it, an unmatched key gets a way
// out that isn't "go read a spec". It sends the raw alias as `feed_id`. That path already exists in
// memberFeedId.js — with no list loaded, the key is used as-is — but becomes unreachable the moment
// a list does load, which is precisely when the list may be the thing that's wrong.
//
// `onOpenCandidate(feedId)` is the ambiguous case's equivalent, and is NOT the same offer. Trying
// anyway is a gamble on an id the list never confirmed; picking a candidate opens an id the list
// itself supplied. Only the second is available here, which is why AMBIGUOUS still refuses
// `allowsTryAnyway`: sending the bare member name would be the guess the reason code exists to stop.
export const DrillDownUnavailableModal = ({
  memberKey,
  memberLabel,
  reason,
  candidates = [],
  onDismiss,
  onOpenAnyway,
  onOpenCandidate,
}) => {
  const explanation = EXPLANATIONS[reason] || EXPLANATIONS[DRILL_DOWN_REASONS.NOT_IN_REGISTRY];
  const label = memberLabel || (memberKey ? formatFeedId(memberKey) : 'This component');
  const canTryAnyway = Boolean(explanation.allowsTryAnyway && onOpenAnyway && memberKey);
  // Both halves have to hold before the copy promises a pick: a parent that can't act on one, or a
  // reason with no rivals to choose between, leaves the analyst reading an instruction it can't follow.
  const canPick = Boolean(explanation.allowsPick && onOpenCandidate && candidates.length > 0);

  return (
    <Dialog label={explanation.title} onDismiss={onDismiss} maxWidth="520px">
      <Body1 bold>{explanation.title}</Body1>

      <Container top={12}>
        <Body5>
          {label}
          {memberKey ? (
            <>
              {' ('}
              <Key>{memberKey}</Key>
              {') '}
            </>
          ) : (
            ' '
          )}
          — {explanation.what}
        </Body5>
      </Container>

      {candidates.length > 0 && (
        <Container top={12}>
          <Body7 bold>{`Matched ${candidates.length} feeds`}</Body7>
          <Container top={4}>
            <CandidateList>
              {candidates.map((candidate) => (
                <Candidate key={candidate}>
                  {canPick ? (
                    <CandidateButton
                      type="button"
                      onClick={() => onOpenCandidate(candidate)}
                      title={`Open ${candidate}`}
                      aria-label={`Open ${candidate}`}
                    >
                      <Body7>{candidate}</Body7>
                    </CandidateButton>
                  ) : (
                    <CandidateText>
                      <Body7>{candidate}</Body7>
                    </CandidateText>
                  )}
                </Candidate>
              ))}
            </CandidateList>
          </Container>
        </Container>
      )}

      <Container top={12}>
        <Body5 isLowContrast>{explanation.why}</Body5>
      </Container>

      <Container top={12}>
        <Body5>{canPick ? explanation.nextWhenPickable : explanation.next}</Body5>
      </Container>

      <Container top={24}>
        <DialogButtonRow>
          {canTryAnyway ? (
            <>
              <TransparentFlatButton onClick={onDismiss}>Close</TransparentFlatButton>
              <FlatButton onClick={() => onOpenAnyway(memberKey)}>Try opening it anyway</FlatButton>
            </>
          ) : (
            <FlatButton onClick={onDismiss}>Close</FlatButton>
          )}
        </DialogButtonRow>
      </Container>
    </Dialog>
  );
};
